/**
 * Deposit settlement reads for the move-out tab.
 *
 * The settlement tables arrive with 20260814130000_deposit_settlements.sql, and
 * that migration is applied by hand. On any database that has not had it yet,
 * PostgREST answers "could not find the table in the schema cache" and fails the
 * whole query — which would put a red error under the move-out tab on every
 * refetch for a condition already known after the first attempt.
 *
 * So a missing table degrades to "no settlement yet" and the card renders its
 * empty state, following the same shape as admin_notes.kind in useProperties:
 * a session-remembered probe whose NEGATIVE answer expires, so applying the
 * migration is noticed without a hard reload.
 *
 * Only a missing TABLE justifies that fallback. Reporting "no settlement" after
 * a transient failure would show a released deposit as unsent, and offer to send
 * a bundle that already went out — a wrong answer is worse than a visible error.
 */

import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import type { DeductionCategory, SettlementRow } from "@/lib/depositSettlement";

export interface DepositDeductionRow {
  id: string;
  settlement_id: string;
  sort_order: number;
  category: DeductionCategory;
  amount_cents: number;
  reason: string;
  evidence_paths: string[];
  created_by_user_id: string | null;
  created_by_name: string | null;
  created_at: string;
}

export interface DepositSettlementResult {
  settlement: (SettlementRow & { id: string; booking_id: number }) | null;
  deductions: DepositDeductionRow[];
  /** False when this database has not had the settlement migration applied. */
  schemaReady: boolean;
}

const SETTLEMENT_COLS = `
  id, booking_id, status, decision,
  deposit_held_cents, deposit_expected_cents, amounts_snapshot_at,
  refund_cents, excess_cents,
  bundle_recipient_email, bundle_recipient_name, bundle_pdf_path,
  bundle_page_count, bundle_size_bytes, bundle_generated_at, bundle_sent_at,
  bundle_blocked_reason,
  review_token_expires_at, review_opened_at, review_reminded_at,
  released_at, released_by_user_id, released_by_name, released_by_role,
  release_note,
  statement_pdf_path, statement_sent_at, statement_tenant_sent_at,
  statement_rep_sent_at,
  refund_paid_at, refund_proof_path, refund_payout_iban_last4,
  created_at, updated_at
`;

/**
 * True when this PostgREST error is specifically "that table isn't there".
 *
 * Exported so the discrimination is testable — everything about the fallback
 * hangs on NOT treating an unrelated failure as "the environment is behind".
 * Note this is deliberately separate from isMissingColumn: a missing table and
 * a missing column are different migrations being absent, and conflating them
 * would let a genuinely broken column selection masquerade as a fresh database.
 */
export function isMissingTable(error: unknown): boolean {
  if (!error || typeof error !== "object") return false;
  const e = error as { code?: string; message?: string };
  if (e.code === "42P01" || e.code === "PGRST205") return true;
  const msg = String(e.message ?? "");
  return (
    /relation .* does not exist/i.test(msg) ||
    /could not find the table/i.test(msg)
  );
}

let schemaSupport: { present: boolean; at: number } | null = null;
const SCHEMA_SUPPORT_TTL_MS = 60_000;

function knownSchemaSupport(): boolean | null {
  if (!schemaSupport) return null;
  if (schemaSupport.present) return true;
  // "The table exists" can never become false; "it is missing" stops being true
  // the moment the migration is applied.
  return Date.now() - schemaSupport.at < SCHEMA_SUPPORT_TTL_MS ? false : null;
}

/** Reset by tests, and after the migration is applied in-session. */
export function resetDepositSettlementSchemaSupport() {
  schemaSupport = null;
}

export function depositSettlementKey(bookingId: number | null | undefined) {
  return ["v2", "deposit-settlement", bookingId ?? null] as const;
}

/**
 * The settlement and its deduction lines for one booking.
 *
 * Deductions are ordered the way they print in the refund statement, so the
 * admin card and the tenant's document list read in the same order.
 */
export function useDepositSettlement(bookingId: number | null | undefined) {
  return useQuery({
    queryKey: depositSettlementKey(bookingId),
    enabled: !!bookingId,
    queryFn: async (): Promise<DepositSettlementResult> => {
      const empty: DepositSettlementResult = {
        settlement: null,
        deductions: [],
        schemaReady: true,
      };
      if (!bookingId) return empty;

      if (knownSchemaSupport() === false) {
        return { ...empty, schemaReady: false };
      }

      const res = await supabase
        .from("deposit_settlements")
        .select(SETTLEMENT_COLS)
        .eq("booking_id", bookingId)
        .maybeSingle();

      if (res.error) {
        if (!isMissingTable(res.error)) throw res.error;
        schemaSupport = { present: false, at: Date.now() };
        return { ...empty, schemaReady: false };
      }
      schemaSupport = { present: true, at: Date.now() };

      const settlement = res.data ?? null;
      if (!settlement) return empty;

      const ded = await supabase
        .from("deposit_deductions")
        .select(
          "id, settlement_id, sort_order, category, amount_cents, reason, evidence_paths, created_by_user_id, created_by_name, created_at"
        )
        .eq("settlement_id", settlement.id)
        .order("sort_order", { ascending: true })
        .order("created_at", { ascending: true });

      if (ded.error) {
        if (!isMissingTable(ded.error)) throw ded.error;
        return { settlement, deductions: [], schemaReady: false };
      }

      return {
        settlement,
        deductions: (ded.data ?? []) as DepositDeductionRow[],
        schemaReady: true,
      };
    },
  });
}
